/* eslint-disable max-lines */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import APIHandler from 'config/APIHandler';

import { useSettings } from 'hooks/useSettings';
import { useUserInfo } from 'hooks/useUserInfo';

import { categoryGroups } from 'settings';

const chaptersInfoContext = createContext();

const defaultValues = {
  loaded: false,
  chapters: [],
  categories: [],
  groups: {},
  percentage: 0,
  subapps: [],
  itineraries: [],
};

/**
 * Provides a context for chapters information. This provider is meant to encapsulate the App component for appwide use.
 *
 * @param {React.ReactNode} children - The children components that can consume the context.
 * @returns {JSX.Element} The provider component.
 */
const ChaptersInfoProvider = ({ children }) => {
  const [chaptersInfo, setChaptersInfo] = useState(defaultValues);
  const {
    appId,
    loaded: loadedSettings,
    lang: { code, langFile },
  } = useSettings();
  const {
    user: { email },
    loaded: loadedUser,
  } = useUserInfo();

  /**
   * @typedef {object} TransformedResponse
   * @property {Chapter[]} chapters - A list of chapters for the current app.
   * @property {Category[]} categories - A list of categories for the current app.
   * @property {number} percentage - The total completion percentage for all chapters within THE APP
   */

  /**
   * Helper function that parses and normalizes the data from the API response.
   *
   * @param {object} response - The API response for APIHandler.getChaptersAndProgress.
   * @returns {TransformedResponse} An object containing the normalized chapters, categories and total completion percentage.
   */
  const transformResponse = (response) => {
    // For future development: response also contains app and user information, but we don't need that here.
    const { totalPercentage, chapters: { categorized = [], uncategorized = [] } = {} } = response;

    // If there are no categorized chapters, return chapters based on the uncategorized chapters + an empty categories and collections array. Primarily used for older apps that have not been updated within cms.
    if (categorized.length === 0) {
      const newUncategorizedChapters = uncategorized[0].map((chapter) => {
        const {
          id,
          image,
          title,
          badge,
          video,
          chapter_category_id: chapterCategoryId,
          coming_soon: comingSoon,
          essentials_chapter: essentialsChapter,
          intro_chapter: introChapter,
          test_chapter: testChapter,
          hidden_in_source: hiddenInSource,
          upcoming,
          specialization_bool: specialisation,
          tier_bool_1: tier1,
          tier_bool_2: tier2,
          tier_bool_3: tier3,
          tier_bool_4: tier4,
          chapterNumber,
          chapterItemCount,
          chapterItemCountMin,
          progress,
          percentage,
          alternative_train: alternativeTrain,
          translations,
        } = chapter;

        return {
          id,
          image,
          title,
          badge,
          video,
          chapterCategoryId,
          comingSoon,
          essentialsChapter,
          introChapter,
          testChapter,
          hiddenInSource,
          upcoming,
          specialisation,
          tier1,
          tier2,
          tier3,
          tier4,
          chapterNumber: Number(chapterNumber),
          chapterItemCount,
          chapterItemCountMin,
          progress,
          percentage,
          alternativeTrain,
          translations,
        };
      });
      return {
        chapters: newUncategorizedChapters,
        categories: [],
        totalPercentage,
      };
    }

    // Create a list of category objects from the categorized chapter collections. chapters is replaced with a list of chapter ids to prevent data duplication.
    const transformedCategoryList = categorized.map((category) => {
      const {
        id,
        title,
        translated_titles: translations,
        badge,
        color,
        image,
        facts,
        translated_facts: translatedFacts,
        states_list: statesList,
        openable_section: openableSection, // Represents if the category is 'openable' and should thus be rendered from subpage (e.g. /main/training/extra) or not (e.g. /main/training)
        chapters = [], // Contains a list of chapter objects, which will be normalized to just the chapter ids below (to prevent data duplication)
        sectionProgressPercentage, // The percentage of chapters completed within just this category
      } = category;

      const chapterIDs = chapters.map((chapter) => chapter.id);

      const newCategory = {
        id,
        title,
        translations,
        badge,
        color,
        image,
        facts,
        translatedFacts,
        statesList,
        openableSection: !!openableSection,
        chapterIDs,
        percentage: sectionProgressPercentage,
      };
      return newCategory;
    });

    // Create a list of chapter objects from all categories.
    const transformedChaptersList = categorized
      .flatMap(({ chapters }) => {
        const newChapter = chapters.map(
          ({
            id,
            image,
            title,
            badge,
            video,
            chapter_category_id: chapterCategoryId, // chapter category id --> maps to collection id above
            coming_soon: comingSoon,
            essentials_chapter: essentialsChapter,
            intro_chapter: introChapter,
            test_chapter: testChapter,
            hidden_in_source: hiddenInSource,
            upcoming,
            specialization_bool: specialisation,
            tier_bool_1: tier1,
            tier_bool_2: tier2,
            tier_bool_3: tier3,
            tier_bool_4: tier4,
            chapterNumber,
            chapterItemCount,
            chapterItemCountMin,
            progress, // contains the latest progress information (number of pages completed) for the page. progress vs chapterItemCount maps to the completion percentage
            percentage, // percentage of chapter completed
            alternative_train: alternativeTrain,
            translations,
          }) => ({
            id,
            image,
            title,
            badge,
            video,
            chapterCategoryId,
            comingSoon,
            essentialsChapter,
            introChapter,
            testChapter,
            hiddenInSource,
            upcoming,
            specialisation,
            tier1,
            tier2,
            tier3,
            tier4,
            chapterNumber: Number(chapterNumber),
            chapterItemCount,
            chapterItemCountMin,
            progress,
            percentage,
            alternativeTrain,
            translations,
          }),
        );

        return newChapter;
      })
      .sort((a, b) => a.chapterNumber - b.chapterNumber);

    return {
      chapters: transformedChaptersList,
      categories: transformedCategoryList,
      percentage: totalPercentage,
    };
  };

  // TODO: Subapps do not implement openable_section; all subapp categories are rendered as a single card linking to the subpage.
  const transformSubappsResponse = (response) => {
    const { categories = [] } = response;
    const list = categories.map(
      ({
        title,
        translations,
        category_percentage: categoryPercentage,
        id,
        image,
        micro_modules: microModules = [],
        facts = [],
      }) => {
        const chapterIDs = microModules.map((chapter) => chapter.id);
        const filtered = microModules.filter(({ sub_app: sub }) => langFile === "en" ? true : !sub.english_only_translation);

        const modules = filtered.map(
          ({
            id: moduleIDs,
            background_image: backgroundImage,
            badge,
            percentage,
            title: moduleTitle,
            session_app_id: sessionAppId,
            ...rest
          }) => ({
            id: moduleIDs,
            image: backgroundImage,
            sessionAppId,
            badge,
            title: moduleTitle,
            percentage,
            ...rest,
          }),
        );
        return {
          title,
          translations,
          id,
          image,
          category_percentage: categoryPercentage,
          chapterIDs,
          modules,
          facts,
        };
      },
    );

    return list;
  };

  const transformItinerariesResponse = (payload) => {
    const { categories = [] } = payload || {};
    return categories.map(
      ({
        title,
        translations = [],
        category_percentage: categoryPercentage,
        id,
        image,
        openable_section: openableSectionRaw,
        micro_modules: microModules = [],
        facts = [],
      }) => {
        const chapterIDs = microModules.map((chapter) => chapter.id);
        const filtered =
          langFile === 'en' ? microModules : microModules.filter(({ sub_app: sub }) => !sub?.english_only_translation);

        const modules = filtered.map(
          ({
            id: moduleIDs,
            background_image: backgroundImage,
            badge,
            percentage,
            title: moduleTitle,
            session_app_id: sessionAppId,
            ...rest
          }) => ({
            id: moduleIDs,
            image: backgroundImage,
            sessionAppId,
            badge,
            title: moduleTitle,
            percentage,
            ...rest,
          }),
        );
        return {
          title,
          translations,
          id,
          image,
          category_percentage: categoryPercentage,
          openableSection: !!openableSectionRaw,
          chapterIDs,
          modules,
          facts,
        };
      },
    );
  };

  const fetchChapterInfo = useCallback(async () => {
    if (!loadedSettings || !loadedUser) {
      return;
    }

    console.log(langFile);
    const locale = `${langFile}-${langFile.toUpperCase()}`;
    const response = await APIHandler.getChaptersAndProgress(appId, email, locale);
    const subappsResponse = await APIHandler.getSubappsNew(appId, email, locale);
    const itinerariesResponse = await APIHandler.getItineraries(appId, email, locale);

    console.log("useChapters:", {
      itinerariesResponse,
    })

    // Check response for errors
    if (response?.response?.status === 404) {
      console.error('User not found');
      await APIHandler.registerUserProgress(email);

      // Refresh page
      window.location.reload();
      return;
    }

    const newChaptersInfo = transformResponse(response);
    const newSubappsInfo = transformSubappsResponse(subappsResponse);

    let itinerariesPayload = null;
    if (itinerariesResponse && Array.isArray(itinerariesResponse.categories)) {
      itinerariesPayload = itinerariesResponse;
    } else if (
      itinerariesResponse &&
      itinerariesResponse.result === 'success' &&
      itinerariesResponse.data
    ) {
      itinerariesPayload = itinerariesResponse.data;
    }

    let newItinerariesInfo = [];
    if (itinerariesPayload) {
      newItinerariesInfo = transformItinerariesResponse(itinerariesPayload);
    }

    setChaptersInfo({ ...newChaptersInfo, loaded: true, subapps: newSubappsInfo, itineraries: newItinerariesInfo });
  }, [appId, email, loadedSettings, loadedUser, langFile, code]);

  // Memoizes the context values to prevent unnecessary re-renders
  const chaptersInfoValues = useMemo(() => {
    const { loaded, chapters, categories, totalPercentage, subapps, itineraries } = chaptersInfo;
    // Organizes all category and chapter ids into their respective groups (traditionally: main/sub/extra, but this is based on the categoryGroups object in settings.js), so that both can easily be accessed from the
    // same group name (for diploma calculation, progressbars per category, etc)
    const groups = {};
    Object.keys(categoryGroups ?? {}).forEach((key) => {
      const { groupCategoryIDs, groupChapterIDs } = categories.reduce(
        (acc, { id, chapterIDs }) => {
          if (categoryGroups[key].some((category) => category === id)) {
            acc.groupCategoryIDs.push(id);
            acc.groupChapterIDs.push(...chapterIDs); // Spread operator to flatten the array
          }
          return acc;
        },
        { groupCategoryIDs: [], groupChapterIDs: [] },
      );

      groups[key.toLowerCase()] = {
        categoryIDs: groupCategoryIDs,
        chapterIDs: groupChapterIDs,
      };
    });

    // Calculates the diploma rate based on the main chapters completion percentage
    const { mainTotal, mainComplete } = chapters
      .filter(({ id }) => groups?.main?.chapterIDs?.includes(id))
      .reduce(
        (acc, chapter) => {
          const { percentage } = chapter;
          acc.mainTotal += 1;
          if (percentage === 100) {
            acc.mainComplete += 1;
          }
          return acc;
        },
        { mainTotal: 0, mainComplete: 0 },
      );

    const diplomaRate = mainTotal > 0 ? (mainComplete / mainTotal) * 100 : 0;

    console.log('pre-return-data:', itineraries)

    return {
      loaded,
      chapters,
      categories,
      groups,
      diplomaRate,
      totalPercentage,
      fetchChapterInfo,
      subapps,
      itineraries,
    };
  }, [chaptersInfo, fetchChapterInfo]);

  return <chaptersInfoContext.Provider value={chaptersInfoValues}>{children}</chaptersInfoContext.Provider>;
};

ChaptersInfoProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * @typedef {object} Chapter
 * @property {string} id - The id of the chapter.
 * @property {string} image - The background/thumbnail image for the chapter.
 * @property {string} title - The title of the chapter.
 * @property {string} badge - The badge/thumbnail image for the chapter (if any).
 * @property {number} chapterCategoryId - The id of the category the chapter belongs to. Maps to the category.id property.
 * @property {boolean} comingSoon - If chapter is coming soon.
 * @property {boolean} essentialsChapter - If chapter is an essentials chapter.
 * @property {boolean} introChapter - If chapter is a intro chapter.
 * @property {boolean} testChapter - If chapter is a test chapter (and therefore should not be included in production builds).
 * @property {boolean} hiddenInSource - If chapter is hidden in "The Source".
 * @property {boolean} upcoming - If chapter is upcoming.
 * @property {boolean} specialisation - If chapter is a specialization chapter.
 * @property {boolean} tier1 - ??
 * @property {boolean} tier2 - ??
 * @property {boolean} tier3 - ??
 * @property {boolean} tier4 - ??
 * @property {number} chapterItemCount - The total number of pages in the chapter.
 * @property {number} chapterItemCountMin - ??
 * @property {number} progress - The latest progress information (number of pages completed) for the page. progress vs
 *   chapterItemCount maps to the completion percentage.
 * @property {number} percentage - The percentage of chapter completed.
 * @property {boolean} alternativeTrain - If chapter is an alternative training chapter.
 */

/**
 * A category is a collection of chapters, grouped together by a common theme/topic. May represent a purely
 * administrative groupping (e.g. 'Main'), where the links to the chapters should be rendered directly from the
 * training-/retainview as individual links, or a functional group, where the category as a whole represents a single
 * button on the training-/retainview, which forwards the user to a subpage where these chapters-links are then rendered.
 *
 * @typedef {object} Category
 * @property {number} id - The id of the category.
 * @property {string} title - The title of the category.
 * @property {string} badge - The badge/thumbnail image for the category (if any).
 * @property {boolean} openableSection - If category is openable, the category chapters should be accessible from a
 *   subpage (e.g. /main/training/extra) or not (e.g. /main/training).
 * @property {number[]} chapterIDs - A list of chapter ids that belong to this category. Maps to the chapter.id property.
 * @property {number} percentage - The total completion percentage for all chapters within THIS CATEGORY.
 */

/**
 * - An object that dynamically organizes the category ids into their respective groups. Each property name corresponds to
 *   a key from the categoryGroup object in the settings config, and the value is an array of category IDs (map to
 *   category.id). Traditionally, properties include 'main', 'sub', and 'extra', but the actual properties may vary.≈
 *
 * @typedef {object} CategoryGroups
 * @property {number[]} [key] - A list of category ids for the current group. The key(s) corresponds to a key from the
 *   categoryGroup object in the settings config. May have any number of keys, and keys are always lowercase.
 */

/**
 * @typedef {object} ChaptersInfo
 * @property {Chapter[]} chapters - A list of chapters for the current app.
 * @property {Category[]} categories - A list of categories for the current app.
 * @property {CategoryGroups} groups - An object that organizes the category ids into groups.
 * @property {number} percentage - The total completion percentage for all chapters within THE APP
 */

/**
 * A custom hook that provides (normalized) data about the chapters, categories and groups for the current app.
 *
 * @returns {ChaptersInfo} An object containing the global app settings.
 */
const useChapters = () => {
  const context = useContext(chaptersInfoContext);

  if (context === undefined) {
    throw new Error('useChapters must be used within a ChaptersInfoProvider');
  }

  const { loaded, chapters, categories, groups, fetchChapterInfo, diplomaRate, subapps, itineraries = [] } = context;

  useEffect(() => {
    fetchChapterInfo();
  }, [fetchChapterInfo]);

  return {
    loaded,
    diplomaRate,
    chapters,
    categories,
    groups,
    subapps,
    itineraries,
  };
};

export { ChaptersInfoProvider, useChapters };
